import { __assign } from "tslib";
import { FrameMessageType } from './constants';
import Messager from './Messager';
import { getLocalStorageFailPromise, isStorageAvailable, onBodyReady } from './utils';
window._XDomainStorageFrames_ = window._XDomainStorageFrames_ || {};
var defaultOptions = {
    emulateSession: true,
    pageTransitionTimeout: 2000,
    sessionTimeout: 86400000, // 24h
};
var pageKey = "" + Math.random();
var id = 0;
// Map message ids to response resolvers
var idResponseMap = {};
/**
 * Get next message id.
 */
function nextId() {
    return "" + id++ + pageKey;
}
/**
 * Wait for an message with specified id responding.
 * @param id Message id.
 */
function waitForResponse(id) {
    return new Promise(function (r) {
        idResponseMap[id] = r;
    });
}
/**
 * Qualify a (maybe relative) URL to absolute one.
 * @param url Original URL.
 */
function qualifyURL(url) {
    var a = document.createElement('a');
    a.href = url;
    return a.cloneNode(false).href;
}
var XDomainStorage = /** @class */ (function () {
    function XDomainStorage(frameURL, options) {
        var _this = this;
        if (frameURL === void 0) { frameURL = 'https://g.alicdn.com/platform/xdomain-storage/0.2.4/frame.html'; }
        if (options === void 0) { options = {}; }
        this._navigationListener = null;
        this._listeners = [];
        this._receivedKey = {};
        if (!isStorageAvailable) {
            return;
        }
        this._options = __assign(__assign({}, defaultOptions), options);
        var params = {
            options: this._options,
            url: location.href.replace(/#.*$/, ''),
            navigationTime: window.performance && performance.timing.navigationStart,
        };
        var url = qualifyURL(frameURL).replace(/(#.*)?$/, "#" + encodeURIComponent(JSON.stringify(params)));
        this._frameURL = url;
        var frame = window._XDomainStorageFrames_[url];
        if (!frame) {
            frame = document.createElement('iframe');
            frame.src = url;
            frame.style.display = 'none';
            frame.setAttribute('data-count', '1');
            frame.setAttribute('data-xreplay-ignored', '');
            onBodyReady(function () {
                document.body.appendChild(frame);
            });
        }
        else {
            frame.setAttribute('data-count', "" + (+(frame.getAttribute('data-count') || 0) + 1));
        }
        this._frame = frame;
        window._XDomainStorageFrames_[url] = frame;
        var messager = new Messager(window, frame.contentWindow);
        messager.onmessage = this._onMessage.bind(this);
        this._messager = messager;
        if (this._options.emulateSession) {
            this._navigationListener = function () {
                var _a;
                (_a = _this._messager) === null || _a === void 0 ? void 0 : _a.post({
                    type: FrameMessageType.UPDATE_SESSION,
                    url: location.href.replace(/#.*$/, ''),
                });
            };
            ['mousedown', 'touchstart', 'beforeunload'].forEach(function (eventType) {
                window.addEventListener(eventType, _this._navigationListener, { capture: true, passive: true });
            });
        }
        messager.post({
            type: FrameMessageType.MOUNT,
        }, true);
    }
    XDomainStorage.prototype._onMessage = function (message) {
        if (message.type == FrameMessageType.GET_STORAGE_RESPONSE || message.type == FrameMessageType.SET_STORAGE_RESPONSE) {
            if (idResponseMap[message.id]) {
                idResponseMap[message.id](message);
                delete idResponseMap[message.id];
            }
        }
        else if (message.type == FrameMessageType.ON_EVENT) {
            if (!this._receivedKey[message.key]) {
                this._receivedKey[message.key] = true;
                this._listeners.forEach(function (l) {
                    if (l.type == message.event) {
                        l.callback(message.data);
                    }
                });
            }
        }
    };
    XDomainStorage.prototype.setItem = function (key, value, session) {
        var _this = this;
        if (!isStorageAvailable) {
            return getLocalStorageFailPromise();
        }
        return new Promise(function (r, rej) {
            var _a;
            try {
                var id_1 = nextId();
                (_a = _this._messager) === null || _a === void 0 ? void 0 : _a.post({
                    type: FrameMessageType.SET_STORAGE,
                    id: id_1,
                    key: key,
                    session: !!session,
                    value: JSON.stringify(value),
                });
                waitForResponse(id_1).then(function (data) {
                    if (data.type == FrameMessageType.SET_STORAGE_RESPONSE) {
                        r();
                    }
                });
            }
            catch (e) {
                rej(e);
            }
        });
    };
    XDomainStorage.prototype.getItem = function (key) {
        var _this = this;
        if (!isStorageAvailable) {
            return getLocalStorageFailPromise();
        }
        return new Promise(function (r, rej) {
            var _a;
            var id = nextId();
            (_a = _this._messager) === null || _a === void 0 ? void 0 : _a.post({
                type: FrameMessageType.GET_STORAGE,
                id: id,
                key: key,
            });
            waitForResponse(id).then(function (data) {
                if (data.type == FrameMessageType.GET_STORAGE_RESPONSE) {
                    try {
                        r(JSON.parse(data.value));
                    }
                    catch (e) {
                        rej(e);
                    }
                }
            });
        });
    };
    XDomainStorage.prototype.removeItem = function (key, session) {
        return this.setItem(key, undefined, session);
    };
    XDomainStorage.prototype.addEventListener = function (type, callback) {
        this._listeners.push({ type: type, callback: callback });
    };
    XDomainStorage.prototype.removeEventListener = function (type, callback) {
        this._listeners = this._listeners.filter(function (l) { return l.type !== type || (callback && l.callback !== callback); });
    };
    XDomainStorage.prototype.dispatchEvent = function (type, data, ignoreSelf) {
        var _a;
        var key = Math.random();
        if (ignoreSelf) {
            this._receivedKey[key] = true;
        }
        (_a = this._messager) === null || _a === void 0 ? void 0 : _a.post({
            type: FrameMessageType.EVENT,
            event: type,
            data: data,
            key: key,
        });
    };
    XDomainStorage.prototype.destroy = function () {
        var _this = this;
        var _a;
        if (this._frame && this._frameURL) {
            var count = +(this._frame.getAttribute('data-count') || 0);
            if (count > 1) {
                this._frame.setAttribute('data-count', "" + (count - 1));
            }
            else {
                this._frame.parentElement.removeChild(this._frame);
                delete window._XDomainStorageFrames_[this._frameURL];
            }
            if (this._navigationListener) {
                ['mousedown', 'touchstart', 'beforeunload'].forEach(function (eventType) {
                    window.removeEventListener(eventType, _this._navigationListener, { capture: true });
                });
            }
        }
        (_a = this._messager) === null || _a === void 0 ? void 0 : _a.destroy();
    };
    return XDomainStorage;
}());
export default XDomainStorage;
// 兼容老版本导出
export var XdomainStorage = XDomainStorage;
